import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Footer } from "risa-oasis-ui_v2";
import { LoaderMessage } from "../../components/loaderMessage/loaderMessage";
import AsterasubmissionModal from "../../components/modals/asteraSubmission/asteraSubmission";
import SubmissionSummary from "../../components/modals/submissionSummary/submissionSummary";
import PdfRender from "../../components/pdfRender/pdfRender";
import { ModalId } from "../../enums/modalId";
import { NycbsPharmaOrderKeys } from "../../enums/nycbsPharmaOrder";
import { OrganizationType } from "../../enums/organizationTypes";
import { useModalOpener } from "../../hooks/useModalOpener";
import {
  closeModal as closeModalNew,
  setOpenedModalId,
} from "../../redux/slice/modalSliceNew";
import { AppDispatch, RootState } from "../../redux/store/store";
import BackArrowBlue from "../../svg/back-arrow-blue";
import { CopyIcon } from "../../svg/copy-icon";
import { calculateAge } from "../../utils/ageCalculator";
import { generateFullName } from "../../utils/generateOrderDataForTable";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";
import { capitalizeString } from "../../utils/stringModifications";
import { getPortalNameFromMetadata } from "../cmmOrder/table/utils/getPortalNameFromMetadata";
import { handleOpenSubmissionSummary } from "../pharmaStpFile/actionHandlers/openSubmissionSummaryHandler";
import { useFetchOrderData } from "./hooks/useFetchOrderData";
import { useOutcomeDocument } from "./hooks/useOutcomeDocument";
import { OutcomeSummaryPanel } from "./outcomeSummaryPanel";

const RequestOutcomeLayout = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const { openedModalId } = useModalOpener();
  const orgId = getOrgIdForFetchExternalWorklist();

  const { data: singleCmmOrderData, loading: singleCmmOrderDataLoading } =
    useSelector((state: RootState) => state.cmmSingleOrder);
  const { metaData } = useSelector((state: RootState) => state.modalSliceNew);

  const [imageLoadFailed, setImageLoadFailed] = useState(false);

  useFetchOrderData(id);
  const { screenshotUrl, isFetchingDoc, errorInFetching, noDocumentFound } =
    useOutcomeDocument(id);

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <div className="request-outcome__container flex h-full flex-col bg-[#F7F9FA] px-[0.9375rem] py-[0.625rem]">
      {singleCmmOrderDataLoading ? (
        <LoaderMessage message="Fetching outcome details..." />
      ) : (
        <>
          <div className="request-outcome__header mb-4">
            <div
              className="request-outcome__header-content flex items-center gap-1 text-h12 font-bold text-tertiaryBlue-4 hover:cursor-pointer"
              onClick={handleGoBack}
            >
              <BackArrowBlue />
              Go Back
            </div>
          </div>
          <div className="request-outcome__body flex flex-1 flex-col overflow-hidden rounded bg-white p-4 pb-0">
            <div className="request-outcome__body--header mb-4">
              <div className="request-outcome__body--header-details flex items-center gap-2">
                <div className="request-outcome__body--page-name text-large font-semiBold leading-6 text-black">
                  Request Outcome
                </div>
                <div className="rounded-[1.875rem] bg-primaryGray-16 px-4 text-small font-semibold leading-6 text-primaryGray-1">
                  {capitalizeString(
                    generateFullName(
                      singleCmmOrderData?.[
                        NycbsPharmaOrderKeys.PatientFirstName
                      ] || "",
                      "",
                      singleCmmOrderData?.[
                        NycbsPharmaOrderKeys.PatientLastName
                      ] || "",
                    ),
                  )}
                  &nbsp;&nbsp;<span>&#8226;</span>&nbsp;&nbsp;
                  {singleCmmOrderData?.[NycbsPharmaOrderKeys.PatientMrn] ?? ""}
                  &nbsp;&nbsp;<span>&#8226;</span>&nbsp;&nbsp;
                  {singleCmmOrderData?.[NycbsPharmaOrderKeys.PatientDob] ?? ""}
                  &nbsp;&nbsp;(
                  {calculateAge(
                    singleCmmOrderData?.[NycbsPharmaOrderKeys.PatientDob] ?? "",
                  )}
                  yrs) &nbsp;&nbsp;<span>&#8226;</span>&nbsp;&nbsp;
                  {singleCmmOrderData?.[
                    NycbsPharmaOrderKeys.DrugNameOncoEmr
                  ]?.split(" ")[0] ?? ""}
                  &nbsp;&nbsp;<span>&#8226;</span>&nbsp;&nbsp;
                  <span className="rounded-[1.875rem] bg-primaryGray-16 text-small font-semibold leading-6 text-primaryGray-1">
                    {singleCmmOrderData?.drug_cohort ?? "--"}
                  </span>
                  &nbsp;&nbsp;<span>&#8226;</span>&nbsp;&nbsp;
                  <span className="rounded-[1.875rem] bg-primaryGray-16 text-small font-semibold leading-6 text-primaryGray-1">
                    {singleCmmOrderData?.drug_class ?? "--"}
                  </span>
                </div>
                <div className="rounded-[1.875rem] bg-primaryGray-16 px-4 text-small font-semibold leading-6 text-primaryGray-1">
                  {singleCmmOrderData?.type ?? ""}
                </div>
                <div className="rounded-[1.875rem] bg-primaryGray-16 px-4 text-small font-semibold leading-6 text-primaryGray-1">
                  {getPortalNameFromMetadata(
                    singleCmmOrderData?.[NycbsPharmaOrderKeys.Metadata],
                  ) ?? ""}
                </div>
                <div className="cmm-result-key ml-auto flex items-center gap-2 text-small">
                  Cmm Key:{" "}
                  <span className="font-bold">
                    {singleCmmOrderData?.[NycbsPharmaOrderKeys.CmmResultKey] ??
                      ""}
                    &nbsp;
                  </span>
                  <div
                    className="cmm-result-key copy-icon mr-2 hover:cursor-pointer"
                    onClick={() => {
                      navigator.clipboard.writeText(
                        singleCmmOrderData?.[
                          NycbsPharmaOrderKeys.CmmResultKey
                        ] ?? "",
                      );
                    }}
                  >
                    <CopyIcon />
                  </div>
                </div>
              </div>
            </div>
            <div className="request-outcome__body-content flex flex-1 overflow-hidden rounded-t-lg border bg-[#F7F9FA]">
              <div className="request-outcome__body-content--left ml-4 mr-4 mt-4 w-1/2 flex-1 overflow-auto">
                {isFetchingDoc && (
                  <LoaderMessage message="Fetching documents..." />
                )}
                {errorInFetching && (
                  <div className="flex h-full items-center justify-center text-red-600">
                    Error fetching document. Please try again later.
                  </div>
                )}
                {!isFetchingDoc &&
                  !errorInFetching &&
                  (screenshotUrl && !imageLoadFailed ? (
                    <div className="flex h-full w-full items-center justify-center overflow-auto bg-gray-50 p-4">
                      <img
                        src={screenshotUrl}
                        alt="CMM Screenshot"
                        className="max-h-full max-w-full object-contain"
                        onError={() => setImageLoadFailed(true)}
                      />
                    </div>
                  ) : screenshotUrl && imageLoadFailed ? (
                    <PdfRender file={screenshotUrl} />
                  ) : noDocumentFound ? (
                    <div className="request-outcome__docs flex h-full items-center justify-center text-h10 text-gray-500">
                      No screenshot found for this order
                    </div>
                  ) : null)}
              </div>
              <div className="request-outcome__body-content--right flex w-1/2 flex-1 flex-col overflow-auto border-l bg-white p-4">
                <OutcomeSummaryPanel
                  outcomeData={singleCmmOrderData?.first_stp_outcome_data}
                  comment={
                    singleCmmOrderData?.[NycbsPharmaOrderKeys.CmmComments]
                  }
                />
              </div>
            </div>
          </div>
          <div className="request-outcome__footer">
            <Footer
              cancelButtonText="Go Back"
              submitButtonText="Submit Cmm Key"
              onCancelClick={() => {
                navigate(`/pharma-pa-worklists/pharma-pa-form/${id}`);
              }}
              onSubmitClick={() => {
                if (orgId === OrganizationType.ASTERA) {
                  dispatch(
                    setOpenedModalId({
                      id: ModalId.ASTERA_SUBMISSION_MODAL,
                      metaData: {
                        data: singleCmmOrderData,
                      },
                    }),
                  );
                } else {
                  handleOpenSubmissionSummary(dispatch, singleCmmOrderData);
                }
              }}
              isDisabled={false}
            />
            {openedModalId === ModalId.SUBMISSION_SUMMARY_MODAL &&
              metaData &&
              "data" in metaData && (
                <SubmissionSummary
                  hideQaSecondStpAndComments
                  onClose={() => {
                    dispatch(closeModalNew());
                  }}
                  metaData={metaData as { data: any }}
                  onSuccess={() => {
                    dispatch(closeModalNew());
                  }}
                />
              )}
            {openedModalId === ModalId.ASTERA_SUBMISSION_MODAL &&
              metaData &&
              "data" in metaData && (
                <AsterasubmissionModal
                  onClose={() => dispatch(closeModalNew())}
                  metaData={metaData as { data: any }}
                />
              )}
          </div>
        </>
      )}
    </div>
  );
};

export default RequestOutcomeLayout;
